import { useState } from 'react'
import { Box, Chip, IconButton, Link, Paper, Stack, Tooltip } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { DeleteIcon, EditIcon } from 'components/Icons'
import Confirm from 'components/Dialog'
import NoRecords from 'components/NoRecords'
import { useBasePath, useMember } from 'components/Providers'
import { destroy } from 'store/member/document'
import { hasFullPermission } from 'store'
import client from 'utils/api'
import { ROWS_PER_PAGE_OPTIONS } from 'constants'


export default function DocumentList({ documents }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const basePath = useBasePath()
  const member = useMember()
  const { employmentType, memberId } = useParams()
  const canEdit = hasFullPermission('student.document')
  const [confirm, setConfirm] = useState(null)
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: ROWS_PER_PAGE_OPTIONS[0] })

  const onConfirm = () => {
    const target = confirm
    setConfirm(null)
    dispatch(destroy({ type: employmentType, memberId, personId: member?.person.id, id: target.id }))
  }

  const download = async row => {
    try {
      const { data: url } = await client.get(`/api/file/${row.file?.uuid}/url`)
      const a = window.document.createElement('a')
      a.href = url
      a.download = row.name
      a.target = '_blank'
      a.click()
    } catch (err) {
      console.log( err.message)
    }
  }

  const booleanChip = value => (
    <Chip
      size="small"
      variant={value ? 'filled' : 'outlined'}
      color={value ? 'success' : 'default'}
      label={value ? t('Yes') : t('No')}
    />
  )

  const columns = [
    {
      field: 'name',
      headerName: t('Name'),
      flex: 1.6,
      minWidth: 200,
      renderCell: ({ row }) => (
        <Link
          href="#"
          underline="hover"
          onClick={event => {
            event.preventDefault()
            download(row)
          }}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 500 }}
        >
          {row.name}
        </Link>
      ),
    },
    {
      field: 'documentType',
      headerName: t('Type'),
      flex: 1,
      minWidth: 130,
      renderCell: ({ value }) => (value ? <Chip size="small" variant="outlined" label={value} /> : '—'),
    },
    {
      field: 'approved',
      headerName: t('Approved'),
      flex: 0.6,
      minWidth: 100,
      renderCell: ({ value }) => booleanChip(value),
    },
    {
      field: 'expirationDate',
      headerName: t('Expiration'),
      flex: 1,
      minWidth: 140,
      renderCell: ({ value }) => (
        <Box component="span" >
          {value ? dayjs(value).format('MMM DD, YYYY') : t('No expiry')}
        </Box>
      ),
    },
    {
      field: 'shared',
      headerName: t('Shared'),
      flex: 0.6,
      minWidth: 100,
      renderCell: ({ value }) => booleanChip(value),
    },
    ...(canEdit
      ? [
          {
            field: 'actions',
            headerName: '',
            flex: 0.6,
            minWidth: 100,
            sortable: false,
            align: 'right',
            headerAlign: 'right',
            renderCell: ({ row }) => (
              <Stack direction="row" spacing={0.5}>
                <Tooltip title={t('Edit')}>
                  <IconButton size="small" onClick={() => navigate(`${basePath}/${row.id}`)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('Delete')}>
                  <IconButton size="small" onClick={() => setConfirm(row)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ),
          },
        ]
      : []),
  ]

  return (
    <Box p={1}>
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <DataGrid
          rows={documents ?? []}
          columns={columns}
          getRowId={row => row.id || row.uid}
          autoHeight
          disableColumnMenu
          disableRowSelectionOnClick
          loading={!documents}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={ROWS_PER_PAGE_OPTIONS}
          sx={{ border: 0 }}
          slots={{
            noRowsOverlay: () => (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', py: 4 }}>
                <NoRecords
                  message={t('No documents yet')}
                  description={t('Uploaded documents will appear here.')}
                />
              </Box>
            ),
          }}
        />
        <Confirm open={Boolean(confirm)} onClose={() => setConfirm(null)} onConfirm={onConfirm} />
      </Paper>
    </Box>
  )
}
